import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";

import { TemplateCategory } from "@/components/confluence/templates/template-categories-search";
import { ConfluenceTemplateMessages } from "@/components/confluence/templates/template-messages.ts";
import { ConfluenceTemplateAnalytics } from "@/components/confluence/templates/util/confluence-templates-analytics.ts";
import {
    AdditionalCategoryKey,
    AdditionalSlugs
} from "@/templates/software/confluence/templates/confluence-templates-page/src/config/category-template-order.ts";

const containerStyles = css({
    display: "flex",
    height: "128px",
    justifyContent: "center",
    flexShrink: 0,
    backgroundColor: "#F8F8F8",
    position: "relative",
    insetBlockStart: -60,
    alignItems: "flex-end",
    paddingBlockEnd: 32,
    [media.only.md]: {
        alignItems: "flex-start",
        flexWrap: "wrap",
        flexDirection: "column",
        height: "207px",
        paddingBlockEnd: 0
    },
    [media.only.sm]: {
        alignItems: "flex-start",
        flexWrap: "wrap",
        flexDirection: "column",
        height: "207px",
        paddingBlockEnd: 0
    },
    [media.below.sm]: {
        alignItems: "flex-start",
        flexWrap: "wrap",
        flexDirection: "column",
        height: "250px",
        paddingBlockEnd: 0
    }
});

const additionalCategoriesTextStyles = css({
    color: "#101214",
    textAlign: "center",
    fontFamily: "var(--bxp-charlie-display-font-family)",
    fontSize: "22px",
    fontWeight: 700,
    lineHeight: "28px",
    marginInlineEnd: "50px",
    [media.only.md]: {
        textAlign: "center",
        marginInlineEnd: "0px",
        marginBlockEnd: "24px",
        marginInlineStart: "40px",
        marginBlockStart: "24px"
    },
    [media.only.sm]: {
        textAlign: "center",
        marginInlineEnd: "0px",
        marginBlockEnd: "24px",
        marginInlineStart: "40px",
        marginBlockStart: "24px"
    },
    [media.below.sm]: {
        textAlign: "center",
        marginInlineEnd: "0px",
        marginBlockEnd: "24px",
        marginInlineStart: "40px",
        marginBlockStart: "24px"
    }
});

const categoryList = css({
    display: "flex",
    flexDirection: "row",
    fontFamily: "var(--bxp-charlie-display-font-family)",
    fontSize: "14px",
    fontWeight: 400,
    letterSpacing: 0.42,
    color: "#101214",
    gap: 64,
    wordWrap: "break-word",
    justifyContent: "space-between",
    margin: 0,
    zIndex: 2,
    [media.only.md]: {
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        rowGap: 8,
        columnGap: 128
    },
    [media.only.sm]: {
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        rowGap: 8,
        columnGap: 128
    },
    [media.below.sm]: {
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        rowGap: 8,
        columnGap: 64
    }
});
const categoryListItem = css({
    listStyleType: "none",
    lineHeight: "28px"
});

interface AdditionalCategoriesSectionProps {
    categories: TemplateCategory[];
}

const AdditionalCategoriesSection = ({
    categories
}: AdditionalCategoriesSectionProps) => {
    const intl = useIntl();
    const locale = intl.locale;

    const localizedLinkForCategory = (categoryId: string) => {
        const prefix = locale === "en" ? "" : `/${locale}`;
        return `${prefix}/software/confluence/templates/categories/${categoryId}`;
    };

    const slugOrder: { [key: string]: number } = AdditionalSlugs.reduce(
        (
            acc: {
                [key: string]: number;
            },
            slug: string,
            index: number
        ) => {
            acc[slug] = index;
            return acc;
        },
        {}
    );

    const additionalCategories = categories
        .filter((category) =>
            AdditionalSlugs.includes(category.urlSlug as AdditionalCategoryKey)
        )
        .sort(
            (a, b) => (slugOrder[a.urlSlug] ?? 0) - (slugOrder[b.urlSlug] ?? 0)
        );
    return (
        <div css={containerStyles}>
            <div css={additionalCategoriesTextStyles}>
                <FormattedMessage
                    {...ConfluenceTemplateMessages.additionalCategoriesText}
                />
            </div>
            <ul css={categoryList}>
                {additionalCategories.map((category) => (
                    <li key={category.id} css={[categoryListItem]}>
                        <a
                            href={localizedLinkForCategory(category?.urlSlug)}
                            {...ConfluenceTemplateAnalytics.viewTemplateCategory(
                                category.urlSlug
                            )}
                        >
                            {category.name}
                        </a>
                    </li>
                ))}
            </ul>
        </div>
    );
};
AdditionalCategoriesSection.displayName = "AdditionalCategoriesSection";
export { AdditionalCategoriesSection };
